import { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Player } from "@lottiefiles/react-lottie-player";
import { Button, Text } from "@adoptaunabuelo/react-components";
import Logo from "../../assets/logo/AUA";
import felicitacionTitleImage from "../../assets/images/felicitacion/FelicitaAbuelo.webp";
import grandpasHappyImage from "../../assets/images/felicitacion/GrandpasHappy.webp";
import cakeAnimation from "../../assets/images/felicitacion/cake-animation.json";

interface Props {
	onContinue: () => void;
	loading?: boolean;
}

const PAGE_BACKGROUND = "#FFFCF8";
const SHEET_COLOR = "#0B3E6D";

const spin = keyframes`
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
`;

const Screen = styled.div`
	position: relative;
	display: flex;
	min-height: 100dvh;
	min-height: 100vh;
	width: 100%;
	justify-content: center;
	background: ${PAGE_BACKGROUND};
	overflow: hidden;
`;

const MobileFrame = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 430px;
	min-height: 100dvh;
	min-height: 100vh;
	box-sizing: border-box;
	overflow: hidden;

	@media (max-height: 700px) {
		padding-top: 12px;
	}
`;

const SplashLayer = styled.div`
	position: absolute;
	inset: 0;
	pointer-events: none;
`;

const CakeLoadingScreen = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 24px;
	text-align: center;
`;

const CakeAnimation = styled(Player)`
	width: min(240px, 70vw);
	height: auto;
`;

const FloatingLogo = styled.div<{ $visible: boolean; $docked: boolean }>`
	position: absolute;
	left: 50%;
	top: 50%;
	z-index: 3;
	display: flex;
	justify-content: center;
	opacity: 0;
	transform: translate(-50%, -50%) scale(0.96);
	transition:
		opacity 0.45s ease,
		top 1.05s cubic-bezier(0.16, 1, 0.3, 1),
		transform 1.05s cubic-bezier(0.16, 1, 0.3, 1);

	${(props) =>
		props.$visible &&
		css`
			opacity: 1;
			top: ${props.$docked ? "32px" : "50%"};
			transform: ${props.$docked
				? "translate(-50%, 0) scale(1)"
				: "translate(-50%, -50%) scale(1)"};
		`}

	@media (max-height: 700px) {
		${(props) =>
			props.$visible &&
			css`
				top: ${props.$docked ? "16px" : "50%"};
			`}
	}

	@media (min-height: 780px) and (max-height: 880px) {
		${(props) =>
			props.$visible &&
			css`
				top: ${props.$docked ? "22px" : "50%"};
			`}
	}

	@media (min-width: 768px) {
		${(props) =>
			props.$visible &&
			css`
				top: ${props.$docked ? "8px" : "50%"};
			`}
	}

	
`;

const SpinnerWrap = styled.div<{ $hidden: boolean }>`
	position: absolute;
	left: 50%;
	top: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: ${(props) => (props.$hidden ? 0 : 1)};
	transform: translate(-50%, -50%);
	transition: opacity 0.3s ease;
`;

const Spinner = styled.div`
	width: 28px;
	height: 28px;
	border-radius: 999px;
	border: 3px solid rgba(0, 71, 115, 0.18);
	border-top-color: #004773;
	animation: ${spin} 0.8s linear infinite;
`;

const WelcomeScreen = styled.div<{ $visible: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: flex-end;
	opacity: 0;
	transform: translateY(180px);
	pointer-events: none;
	padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
	transition:
		opacity 0.7s ease,
		transform 1.25s cubic-bezier(0.18, 1, 0.22, 1);
	overflow: auto;

	${(props) =>
		props.$visible &&
		css`
			opacity: 1;
			transform: translateY(0);
			pointer-events: auto;
		`}
`;

const BluePanel = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
	margin: 40px 0 0;
	padding: 52px 24px 0;
	border-radius: 28px;
	background: ${SHEET_COLOR};
	color: white;
	overflow: hidden;

	@media (max-height: 760px) {
		margin-top: 85px;
		padding: 36px 24px 0;
		border-radius: 24px;
	}

	@media (max-width: 430px) and (min-height: 780px) and (max-height: 880px) {
		margin-top: 50px;
	}

	@media (min-height: 900px) {
		padding: 74px 24px 0;
	}

	@media (max-height: 700px) {
		margin-top: 20px;
		padding: 28px 16px 0;
	}

`;

const CopyGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	align-items: center;

	@media (max-height: 760px) {
		gap: 18px;
	}

	@media (max-height: 700px) {
		gap: 14px;
	}
`;

const TitleWrap = styled.div`
	display: flex;
	justify-content: center;
	width: 100%;
`;

const Subtitle = styled(Text)`
	color: var(--text-invert, #fff);
	text-align: center;
	font-feature-settings: "liga" off, "clig" off;
	font-family: "Poppins";
	font-size: 15px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;

	@media (max-height: 760px) {
		font-size: 14px !important;
		line-height: 21px !important;
	}

	@media (max-height: 700px) {
		font-size: 13px !important;
		line-height: 19px !important;
	}
`;

const TitleImage = styled.img`
	width: 200.38px;
	max-width: 100%;
	height: 68px;
	object-fit: contain;

	@media (max-height: 760px) {
		width: 170px;
		height: auto;
	}

	@media (max-height: 700px) {
		width: 150px;
		height: auto;
	}
`;

const IllustrationWrap = styled.div`
	display: flex;
	justify-content: center;
	margin-top: auto;
	padding-top: clamp(56px, 16vh, 150px);
	margin-left: -24px;
	margin-right: -24px;

	@media (max-height: 760px) {
		padding-top: clamp(20px, 6vh, 56px);
	}

	@media (min-height: 780px) and (max-height: 880px) {
		padding-top: clamp(84px, 5vh, 120px);
	}

	@media (min-height: 900px) {
		padding-top: clamp(126px, 10vh, 116px);
	}

	@media (max-height: 700px) {
		padding-top: 12px;
	}
`;

const Illustration = styled.img`
	width: 100%;
	max-width: none;
	height: auto;
	object-fit: cover;
`;

const FooterAction = styled.div`
	flex-shrink: 0;
	padding: 18px 20px 0;

	@media (max-height: 760px) {
		padding: 12px 16px 0;
	}

	@media (max-height: 700px) {
		padding: 10px 12px 0;
	}
`;

const FelicitacionLoading = ({ onContinue, loading }: Props) => {
	const [showLogo, setShowLogo] = useState(false);
	const [showWelcome, setShowWelcome] = useState(false);

	useEffect(() => {
		if (loading) {
			setShowLogo(false);
			setShowWelcome(false);
			return;
		}

		const logoTimer = window.setTimeout(() => {
			setShowLogo(true);
		}, 1000);

		const welcomeTimer = window.setTimeout(() => {
			setShowWelcome(true);
		}, 1850);

		return () => {
			window.clearTimeout(logoTimer);
			window.clearTimeout(welcomeTimer);
		};
	}, [loading]);

	if (loading) {
		return (
			<Screen>
				<MobileFrame>
					<CakeLoadingScreen>
						<CakeAnimation
							autoplay
							keepLastFrame
							loop
							src={cakeAnimation}
							style={{ width: "min(240px, 70vw)", height: "auto" }}
						/>
						<Text type="h4" weight="semibold">
							Buscando al cumpleañero...
						</Text>
					</CakeLoadingScreen>
				</MobileFrame>
			</Screen>
		);
	}

	return (
		<Screen>
			<MobileFrame>
				<SplashLayer>
					<SpinnerWrap $hidden={showLogo}>
						<Spinner />
					</SpinnerWrap>
					<FloatingLogo $visible={showLogo} $docked={showWelcome}>
						<Logo size={{ width: 176, height: 58 }} />
					</FloatingLogo>
				</SplashLayer>

				<WelcomeScreen $visible={showWelcome}>
					<BluePanel>
						<CopyGroup>
							<TitleWrap>
								<TitleImage
									alt="Felicita a un Abuelo"
									src={felicitacionTitleImage}
								/>
							</TitleWrap>

							<Subtitle type="p">
								Estas a un paso de conocer a tu abuelo.
								Necesitamos verificar tu identidad para entregar
								correctamente la felicitacion.
							</Subtitle>
						</CopyGroup>

						<IllustrationWrap>
							<Illustration
								alt="Mayores sonriendo"
								src={grandpasHappyImage}
							/>
						</IllustrationWrap>
					</BluePanel>

					<FooterAction>
						<Button style={{ width: "100%" }} onClick={onContinue}>
							Empezar
						</Button>
					</FooterAction>
				</WelcomeScreen>
			</MobileFrame>
		</Screen>
	);
};

export default FelicitacionLoading;
